import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Send, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api, errMsg } from "@/lib/api";
import { Reveal, Overline } from "./Reveal";

const EMPTY = { name: "", email: "", phone: "", social: "", message: "" };

export const Contact = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...form, lang });
      toast.success(c.ok);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(errMsg(err, t.common.error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-40 overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 -z-10">
        <img src="/img/aura-agency-square.jpg" alt="" className="h-full w-full object-cover opacity-15 blur-lg scale-110" />
        <div className="absolute inset-0 bg-[#050713]/70" />
      </div>
      <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal><Overline>{c.over}</Overline></Reveal>
          <Reveal delay={0.08}><h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-white">{c.title}</h2></Reveal>
          <Reveal delay={0.14}><p className="mt-6 text-slate-300 leading-relaxed">{c.sub}</p></Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 glass rounded-[1.5rem] p-6" data-testid="contact-member-card">
              <p className="font-display text-xl text-white">{c.sideTitle}</p>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">{c.sideDesc}</p>
              <Link to="/accedi" data-testid="contact-register-link" className="mt-5 inline-flex items-center gap-2 text-sky-300 hover:text-white transition-colors font-semibold text-sm">
                {c.sideCta} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="glass rounded-[2rem] p-6 md:p-10 grid sm:grid-cols-2 gap-4" data-testid="contact-form">
            <input required minLength={2} value={form.name} onChange={set("name")} placeholder={c.name} className="field" data-testid="contact-name-input" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder={c.email} className="field" data-testid="contact-email-input" />
            <input required type="tel" value={form.phone} onChange={set("phone")} placeholder={c.phone} className="field" data-testid="contact-phone-input" />
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-mono text-sm text-sky-300">@</span>
              <input value={form.social} onChange={set("social")} placeholder={c.social} className="field w-full !pl-9" autoComplete="off" spellCheck={false} data-testid="contact-social-input" />
            </div>
            <textarea required minLength={10} rows={6} value={form.message} onChange={set("message")} placeholder={c.message} className="field sm:col-span-2 resize-none" data-testid="contact-message-input" />
            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
              {sent ? (
                <p className="flex items-center gap-2 text-emerald-300 text-sm" data-testid="contact-success"><CheckCircle2 className="w-4 h-4" /> {c.ok}</p>
              ) : <span />}
              <button disabled={busy} className="btn-chrome rounded-full px-7 py-3 font-semibold text-[#050713] flex items-center gap-2 disabled:opacity-60" data-testid="contact-submit-button">
                {busy ? c.sending : c.submit} <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
